import { useMemo, useState } from 'react';

import {
    DEFAULT_PAGE_SIZE,
    type PagedQueryArg,
    type QueryWhereValue,
} from 'src/apiClients/queries';
import {
    DEFAULT_DATA_TABLE_QUERY_STATE,
    SortDirection,
    type DataTableQueryState,
    type DataTableSort,
} from 'src/components/data-table/DataTable.types';
import { useDebouncedValue } from 'src/hooks/use-debounced-value';

const SEARCH_DEBOUNCE_MS = 300;

export interface UseEntityListQueryState {
    queryState: DataTableQueryState;
    setQueryState: React.Dispatch<React.SetStateAction<DataTableQueryState>>;
    queryArg: PagedQueryArg;
}

/**
 * Centralizes the query-state plumbing every paged list page repeats:
 * controlled DataTable state, debounced search, and translation of
 * sorts/filters/search into the server-side `{ limit, order, where }` shape.
 */
export const useEntityListQueryState = (
    searchColumns: readonly string[],
    initialSort: DataTableSort[],
    baseWhere?: Record<string, QueryWhereValue>,
    baseExtraParams?: Record<string, string>,
): UseEntityListQueryState => {
    const [queryState, setQueryState] = useState<DataTableQueryState>(() => ({
        ...DEFAULT_DATA_TABLE_QUERY_STATE,
        sorts: initialSort,
    }));
    const debouncedSearch = useDebouncedValue(queryState.search, SEARCH_DEBOUNCE_MS);

    const queryArg = useMemo<PagedQueryArg>(() => {
        const order = queryState.sorts.map(
            (s) =>
                [s.field, s.direction === SortDirection.Desc ? 'DESC' : 'ASC'] as [
                    string,
                    'ASC' | 'DESC',
                ],
        );
        const where: Record<string, QueryWhereValue> = { ...baseWhere };
        for (const [field, values] of Object.entries(queryState.filters)) {
            if (values.length > 0) {
                where[field] =
                    values.length === 1 ? (values[0] as QueryWhereValue) : { $or: values };
            }
        }
        if (debouncedSearch) {
            const pattern = `%${debouncedSearch}%`;
            where.$or = searchColumns.map((col) => ({ [col]: { $iLike: pattern } }));
        }
        return { limit: DEFAULT_PAGE_SIZE, order, where, extraParams: baseExtraParams };
    }, [
        queryState.sorts,
        queryState.filters,
        debouncedSearch,
        searchColumns,
        baseWhere,
        baseExtraParams,
    ]);

    return { queryState, setQueryState, queryArg };
};
